import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ICodeService } from '../../services/icode.service';
import {
	IVerificationCodeInfo,
	TwoFactorAuthenticationInfo,
	VerificationCodeInfo,
} from '../../classes/http-responses';
import { IErrorDescription } from '@ibank-globals/error/src/global-error.service';
import { ErrorHandler } from '../../../globals/classes/errorHandler';
import { BaseComponent } from '../../../globals/classes/base.component';
import { TwofaService } from '../../services/twofa.service';
import { UserData } from '../../../globals/classes/userData';
import { ImpersonationService } from '../../services/impersonation.service';
import { finalize } from 'rxjs/operators/finalize';
import { AppSettings } from '../../app.settings';

@Component({
	selector: 'two-fa-modal',
	template: require('./two-fa-modal.component.html'),
})
export class TwoFAModalComponent extends BaseComponent implements OnInit, AfterViewInit {
	//#region Inputs & Outputs
	@Input() twoFAInfo: TwoFactorAuthenticationInfo;
	//#endregion

	//#region public properties
	public title = 'Σύνδεση χρήστη';
	public isLoading = false;
	public rememberConnection: boolean = true;
	public isRebrading = AppSettings.isRebrading;
	//#endregion

	//#region private properties
	private _icode: string;
	private _userId: string;
	public get isImpersonation() {
		return this.twoFAInfo.reasonInfo.reasonCode === '202';
	}
	public get rememberMeEnabled() {
		return this.twoFAInfo.reasonInfo.reasonCode !== '202';
	}
	//#endregion

	//#region ctor
	constructor(private activeModalService: NgbActiveModal, private icodeService: ICodeService) {
		super();
	}
	//#endregion

	//#region  getters & setters
	public get canSubmit(): boolean {
		return this.isValidIcode();
	}
	//#endregion

	//#region component lifecycle methods
	public ngOnInit(): void {
		this.setupSubscriptions();
	}

	public ngAfterViewInit(): void {
		this.icodeService.resetTimer(
			!!this.twoFAInfo.verificationCodeInfo
				? this.twoFAInfo.verificationCodeInfo.codeLifetimeInSeconds
				: 0
		);
		if (this.twoFAInfo.verificationCodeInfo == null) {
			this.handleResendCodeError({ desc: this.twoFAInfo.reasonInfo.reasonDescription }, true);
		}
	}
	//#endregion

	//#region public methods
	public closeModal() {
		this.activeModalService.close();
	}

	public toggleSwitch() {
		if (!this.rememberMeEnabled) return;
		this.rememberConnection = !this.rememberConnection;
	}

	public submit() {
		if (!this.canSubmit) return;
		this.isLoading = true;
		if (this.isImpersonation) {
			this.impesonationLogin();
		} else {
			this.twofaLogin();
		}
	}

	//#endregion

	//#region  private methods
	private isValidIcode(): boolean {
		return this.icodeService.isValidCode(this._icode);
	}

	private setupSubscriptions() {
		this.mortalize(this.icodeService.code$).subscribe((code) => (this._icode = code));
		this.mortalize(this.icodeService.codeRequested$).subscribe(() => this.resendCode());
		this.mortalize(UserData.data$).subscribe(
			(userData) => (this._userId = !!userData && userData.userId)
		);
	}

	private resendCode() {
		this.isLoading = true;
		if (this.isImpersonation) {
			this.resendImpersonationOtp();
		} else {
			this.resendTwofaOtp();
		}
	}

	private twofaLogin() {
		TwofaService.login(this._userId, this._icode, this.rememberConnection)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(response) => {
					this.setDynatraceEvent('Login_Homepage_Loaded_successfully');
				},
				(error) => this.handleSubmitError(error)
			);
	}

	private impesonationLogin() {
		ImpersonationService.login(this._userId, this._icode)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(response) => { },
				(error) => this.handleSubmitError(error)
			);
	}

	private resendTwofaOtp() {
		TwofaService.resendOtp(this._userId)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(verificationCodeInfo) => this.verificationCodeInfoUpdated(verificationCodeInfo),
				(error) => this.handleResendCodeError(error)
			);
	}

	private resendImpersonationOtp() {
		ImpersonationService.checkUserStatus(this._userId)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(usertStatusResponse) =>
					this.verificationCodeInfoUpdated(
						usertStatusResponse.additionalAuthenticationInfo.verificationCodeInfo
					),
				(error) => this.handleResendCodeError(error)
			);
	}

	private verificationCodeInfoUpdated(verificationCodeInfo: IVerificationCodeInfo): void {
		this.twoFAInfo.verificationCodeInfo = new VerificationCodeInfo(verificationCodeInfo);
		this.icodeService.resetTimer(this.twoFAInfo.verificationCodeInfo.codeLifetimeInSeconds);
	}

	private handleResendCodeError(error: IErrorDescription, keepModalOpen: boolean = false) {
		if (!keepModalOpen) {
			this.activeModalService.close();
		}
		ErrorHandler.newError(error);
	}

	private handleSubmitError(error: IErrorDescription) {
		ErrorHandler.newError(error);
	}
	//#endregion
}
